import { SimpleIconButton } from '@dreamstack/feature-components/src/layout/SimpleIconButton'
import { useFormatNumber, useLocale } from '@dreamstack/i18n'
import { Minus } from '@dreamstack/icons/src/icon-components/Minus'
import { Plus } from '@dreamstack/icons/src/icon-components/Plus'
import { SimpleInput } from '@dreamstack/simple-components/src/lib/SimpleInput'
import { InputAdornment } from '@mui/material'
import type { FunctionComponent } from 'react'
import { useState } from 'react'
import {
  formatOptionsDecimal1,
  formatOptionsDecimal2,
  processUserInput,
} from './BTLCalcUtil'

const ROUNDING_PRECISION = 1000

export const BTLCalcField: FunctionComponent<
  React.PropsWithChildren<{
    text: string
    value: number
    maxValue: number
    step: number
    decimals: number
    handler: Function
  }>
> = (props) => {
  const { text, value, maxValue, decimals, step, handler } = props
  const format = useFormatNumber(
    decimals === 1 ? formatOptionsDecimal1 : formatOptionsDecimal2
  )

  const locale = useLocale()

  const [textValue, setTextValue] = useState(format(value))
  const [errorText, setErrorText] = useState('')

  return (
    <div className="flex flex-col md:flex-row mt-px-32">
      <div className="flex-1 flex flex-col justify-center text-left lg:text-right md:pr-px-24 pb-px-24 md:pb-px">
        <div className="font-semibold text-[16px]">{text}</div>
      </div>
      <div className="flex-1 flex justify-start flex-row md:pl-px-24">
        <div className="w-full md:w-[112px]">
          <SimpleInput
            className="flex-1 w-full"
            value={textValue}
            onBlur={() => {
              if (errorText) {
                setErrorText('')
              }
              setTextValue(format(value))
            }}
            onChange={(e) => {
              setTextValue(e.target.value)
              processUserInput(
                e.target.value,
                locale,
                maxValue,
                setErrorText,
                handler
              )
            }}
            inputProps={{
              style: {
                padding: 12,
                fontWeight: 600,
                fontSize: '20px',
              },
              inputMode: 'decimal',
            }}
            InputProps={{
              disableUnderline: true,
              endAdornment: (
                <InputAdornment position="end">
                  <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                    {'%'}
                  </span>
                </InputAdornment>
              ),
            }}
          />
          {errorText && (
            <div className="text-[13px] text-accentroRed-full">{errorText}</div>
          )}
        </div>

        <SimpleIconButton
          aria-label="Increase value"
          className="flex-1 h-[50px] w-[50px] mt-px! ml-px-8! border-black! text-black!"
          onClick={() =>
            handler((v: number) => {
              const tmp = // needed to prevent JS float rounding issue
                (Math.round(ROUNDING_PRECISION * v) +
                  Math.round(ROUNDING_PRECISION * step)) /
                ROUNDING_PRECISION
              if (tmp <= maxValue) {
                setTextValue(format(tmp))
                return tmp
              } else {
                setTextValue(format(v))
                return v
              }
            })
          }
        >
          <Plus />
        </SimpleIconButton>

        <SimpleIconButton
          aria-label="Decrease value"
          className="flex-1 h-[50px] w-[50px] mt-px! ml-px-8! border-black! text-black!"
          onClick={() =>
            handler((v: number) => {
              const tmp = // needed to prevent JS float rounding issue
                (Math.round(ROUNDING_PRECISION * v) -
                  Math.round(ROUNDING_PRECISION * step)) /
                ROUNDING_PRECISION
              if (tmp >= 0) {
                setTextValue(format(tmp))
                return tmp
              } else {
                setTextValue(format(v))
                return v
              }
            })
          }
        >
          <Minus />
        </SimpleIconButton>
      </div>
    </div>
  )
}
